import { ErrorOutline, InboxOutlined } from "@mui/icons-material";
import { Box, Typography } from "@mui/material";
import { ReactNode } from "react";
import AppButton from "../../../../views/components/common/buttons/AppButton";
import CustomTypography from "../../../../views/components/common/form/CustomTypography";
import { swayyColors, swayyFont, swayyRadii } from "../../theme/tokens";

interface SwayyPageHeaderProps {
  module?: string;
  title: string;
  description?: string;
  actions?: ReactNode;
}

/** Brand page header: muted module eyebrow + Pine title + optional right actions. */
export const SwayyPageHeader = ({
  module,
  title,
  description,
  actions,
}: SwayyPageHeaderProps) => (
  <Box
    sx={{
      mb: 1.5,
      display: "flex",
      flexDirection: { xs: "column", sm: "row" },
      alignItems: { xs: "stretch", sm: "flex-start" },
      justifyContent: "space-between",
      gap: 1.5,
    }}
  >
    <Box sx={{ minWidth: 0, flex: 1 }}>
      {module ? (
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 500,
            color: swayyColors.textSecondary,
            fontFamily: swayyFont,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            mb: 0.25,
          }}
        >
          {module}
        </Typography>
      ) : null}
      <Typography
        sx={{
          fontSize: { xs: 22, md: 28 },
          fontWeight: 700,
          color: swayyColors.pine,
          fontFamily: swayyFont,
          letterSpacing: "-0.01em",
          lineHeight: 1.2,
        }}
      >
        {title}
      </Typography>
      {description && (
        <Typography
          sx={{
            mt: 0.5,
            fontSize: 14,
            color: swayyColors.textSecondary,
            fontFamily: swayyFont,
            maxWidth: 560,
          }}
        >
          {description}
        </Typography>
      )}
    </Box>
    {actions && (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: 1.5,
          flexShrink: 0,
          flexWrap: "wrap",
          ml: "auto",
          alignSelf: { xs: "flex-end", sm: "flex-start" },
        }}
      >
        {actions}
      </Box>
    )}
  </Box>
);

interface SwayyTableEmptyStateProps {
  title: string;
  description: string;
  actionLabel?: string;
  onAction?: () => void;
  danger?: boolean;
}

export const SwayyTableEmptyState = ({
  title,
  description,
  actionLabel,
  onAction,
  danger,
}: SwayyTableEmptyStateProps) => (
  <Box
    sx={{
      py: 8,
      px: 3,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      textAlign: "center",
    }}
  >
    <Box
      sx={{
        width: 64,
        height: 64,
        borderRadius: swayyRadii.full,
        bgcolor: danger ? "rgba(220, 38, 38, 0.1)" : swayyColors.pineLight,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        mb: 2,
      }}
    >
      {danger ? (
        <ErrorOutline sx={{ fontSize: 28, color: swayyColors.danger }} />
      ) : (
        <InboxOutlined sx={{ fontSize: 28, color: swayyColors.pine }} />
      )}
    </Box>
    <CustomTypography
      variant="heading6"
      color={swayyColors.deepGreen}
      sx={{ fontFamily: swayyFont, fontWeight: 600, mb: 0.75 }}
    >
      {title}
    </CustomTypography>
    <CustomTypography
      variant="body2"
      color={swayyColors.textSecondary}
      sx={{ fontFamily: swayyFont, maxWidth: 360, mb: actionLabel ? 2.5 : 0 }}
    >
      {description}
    </CustomTypography>
    {actionLabel && onAction && (
      <AppButton
        label={actionLabel}
        onClick={onAction}
        customStyles={{
          minWidth: 140,
          height: 40,
          minHeight: 40,
          borderRadius: swayyRadii.md,
          bgcolor: danger ? swayyColors.danger : swayyColors.pine,
          color: swayyColors.white,
          "&:hover": {
            bgcolor: danger ? "#B91C1C" : swayyColors.pineHover,
          },
        }}
      />
    )}
  </Box>
);
